import { Box, Drawer } from "@mui/material";
import {
  useFees,
  usePaystackPlans,
  usePlanPrivileges,
} from "../../hooks/useSubscribtionDashboard";
import { useState, useEffect, useMemo } from "react";
import BasicInformationSection from "../../components/subscribtionDashboard/layout/BasicInformationSection";
import PricingSection from "../../components/subscribtionDashboard/layout/PricingSection";
import FeaturesSection from "../../components/subscribtionDashboard/layout/FeaturesSection";
import PrivilegesSection from "../../components/subscribtionDashboard/layout/PrivilegesSection";
import FeesSection from "../../components/subscribtionDashboard/layout/FeesSection";
import AdditionalSettingsSection from "../../components/subscribtionDashboard/layout/AdditionalSettingsSection";
import ConfirmationDrawer from "../../components/subscribtionDashboard/layout/ConfirmationDrawer";
import { getErrorMessage } from "../../utils/error";

function EditPlan({
  drawerOpen,
  handleCloseDrawer,
  selectedPlan,
  formData,
  setFormData,
  handleInputChange,
  handleProceedToConfirmation,
  showConfirmation,
  handleBackToForm,
  handleFinalSubmit,
  isPendingUpdate,
  isPending,
}) {
  const { data: feesData, isLoading: isFeesLoading } = useFees({
    limit: 100,
    offset: 0,
  });
  const {
    data: privilegesData,
    isLoading: isPrivilegesLoading,
    isError: isPrivilegesError,
    error: privilegesErrorObj,
    refetch: refetchPrivileges,
  } = usePlanPrivileges();
  const { data: paystackPlansData, isLoading: isPaystackPlansLoading } =
    usePaystackPlans();
  const [feesList, setFeesList] = useState([]);
  const [errors, setErrors] = useState({});

  const formatPaystackAmount = (amount, currency = "NGN") => {
    const normalizedAmount = Number(amount || 0) / 100;

    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(normalizedAmount);
  };

  const paystackPlanOptions = useMemo(() => {
    const paystackPlans =
      paystackPlansData?.data ??
      paystackPlansData?.data?.data ??
      paystackPlansData?.data?.plans ??
      [];

    if (!Array.isArray(paystackPlans)) return [];

    return paystackPlans
      .filter((plan) => !plan?.is_deleted && !plan?.is_archived)
      .map((plan) => ({
        value: plan.plan_code,
        label: `${plan.name} | ${plan.interval} | ${formatPaystackAmount(plan.amount, plan.currency)}`,
        meta: plan,
        amountLabel: formatPaystackAmount(plan.amount, plan.currency),
      }));
  }, [paystackPlansData]);

  const getPaystackPlanLabel = (planCode) => {
    if (!planCode) return "-";

    const matchedPlan = paystackPlanOptions.find(
      (option) => option.value === planCode,
    );

    return matchedPlan?.label || planCode;
  };

  const privilegeOptions = useMemo(() => {
    const options = privilegesData?.data ?? privilegesData?.data?.data ?? [];
    return Array.isArray(options) ? options : [];
  }, [privilegesData]);
  const privilegesErrorMessage = getErrorMessage(privilegesErrorObj);

  useEffect(() => {
    if (feesData) {
      let fees = [];
      if (Array.isArray(feesData)) {
        fees = feesData;
      } else if (feesData.data?.data && Array.isArray(feesData.data.data)) {
        fees = feesData.data.data;
      } else if (feesData.data?.data && Array.isArray(feesData.data?.data)) {
        fees = feesData.data?.data;
      } else if (feesData.data?.data && Array.isArray(feesData.data?.data)) {
        fees = feesData.data?.data;
      }

      setFeesList(fees);
    }
  }, [feesData]);

  useEffect(() => {
    if (selectedPlan && feesList.length > 0) {
      let selectedFeeIds = [];

      if (selectedPlan.fees && Array.isArray(selectedPlan.fees)) {
        selectedFeeIds = selectedPlan.fees
          .map((fee) => {
            if (!fee) return null;

            if (typeof fee === "string") {
              return fee;
            }

            if (typeof fee === "object") {
              const feeId = fee._id || fee.id;
              if (feeId) {
                return feeId.toString();
              }

              const matchingFee = feesList.find(
                (f) =>
                  f.description === fee.description ||
                  (f.minimumFlightIssued === fee.minimumFlightIssued &&
                    f.maximumFlightIssued === fee.maximumFlightIssued),
              );

              if (matchingFee) {
                return (matchingFee._id || matchingFee.id).toString();
              }
            }

            return null;
          })
          .filter((id) => id !== null);
      }

      selectedFeeIds = [...new Set(selectedFeeIds)];

      setFormData((prev) => ({
        ...prev,
        fees: selectedFeeIds,
      }));
    }
  }, [selectedPlan, feesList, setFormData]);

  useEffect(() => {
    if (!drawerOpen) {
      setErrors({});
    }
  }, [drawerOpen]);

  const validateForm = () => {
    const nextErrors = {};
    const requiredFields = [
      {
        key: "name",
        label: "Plan name",
      },
      {
        key: "price",
        label: "Price",
      },
      {
        key: "description",
        label: "Description",
      },
    ];

    requiredFields.forEach((field) => {
      if (!String(formData[field.key] || "").trim()) {
        nextErrors[field.key] = `${field.label} is required.`;
      }
    });

    if (!Array.isArray(formData.features) || formData.features.length === 0) {
      nextErrors.features = "Add at least one feature.";
    }

    if (
      !Array.isArray(formData.privileges) ||
      formData.privileges.length === 0
    ) {
      nextErrors.privileges = "Select at least one privilege.";
    }

    if (!Array.isArray(formData.fees) || formData.fees.length === 0) {
      nextErrors.fees = "Select at least one fee.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleFormInputChange = (event) => {
    const fieldName = event?.target?.name;

    if (fieldName && errors[fieldName]) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors[fieldName];
        return nextErrors;
      });
    }

    handleInputChange(event);
  };

  const handleReviewPlan = () => {
    if (!validateForm()) return;
    handleProceedToConfirmation();
  };

  const handleAddFeature = (featureValue) => {
    if (errors.features) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors.features;
        return nextErrors;
      });
    }

    setFormData((prev) => {
      const currentFeatures = Array.isArray(prev.features) ? prev.features : [];
      const normalizedFeature = featureValue.trim();

      if (!normalizedFeature || currentFeatures.includes(normalizedFeature)) {
        return prev;
      }

      return {
        ...prev,
        features: [...currentFeatures, normalizedFeature],
      };
    });

    const currentFeatures = Array.isArray(formData.features) ? formData.features : [];
    return Boolean(featureValue.trim()) && !currentFeatures.includes(featureValue.trim());
  };

  const handleRemoveFeature = (featureValue) => {
    setFormData((prev) => {
      const currentFeatures = Array.isArray(prev.features) ? prev.features : [];

      return {
        ...prev,
        features: currentFeatures.filter((feature) => feature !== featureValue),
      };
    });
  };

  const handleFeeSelection = (feeId) => {
    if (errors.fees) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors.fees;
        return nextErrors;
      });
    }

    setFormData((prev) => {
      const currentFees = Array.isArray(prev.fees) ? prev.fees : [];
      const feeExists = currentFees.includes(feeId);

      let newFees;
      if (feeExists) {
        newFees = currentFees.filter((id) => id !== feeId);
      } else {
        newFees = [...currentFees, feeId];
      }

      return {
        ...prev,
        fees: newFees,
      };
    });
  };

  const handleSelectAllFees = () => {
    if (errors.fees) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors.fees;
        return nextErrors;
      });
    }

    if (feesList.length > 0) {
      const allFeeIds = feesList.map((fee) => (fee._id || fee.id).toString());
      setFormData((prev) => ({
        ...prev,
        fees: allFeeIds,
      }));
    }
  };

  const handleDeselectAllFees = () => {
    setFormData((prev) => ({
      ...prev,
      fees: [],
    }));
  };

  const handleSelectAllPrivileges = () => {
    if (errors.privileges) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors.privileges;
        return nextErrors;
      });
    }

    setFormData((prev) => ({
      ...prev,
      privileges: privilegeOptions.map((opt) => opt.value),
    }));
  };

  const handleDeselectAllPrivileges = () => {
    setFormData((prev) => ({
      ...prev,
      privileges: [],
    }));
  };

  const isFeeSelected = (feeId) => {
    if (!feeId) return false;

    // Convert both to strings for comparison
    const stringFeeId = feeId.toString();
    return (
      Array.isArray(formData.fees) &&
      formData.fees.some((id) => id?.toString() === stringFeeId)
    );
  };

  const handlePrivilegeChange = (privilegeValue) => {
    if (errors.privileges) {
      setErrors((prev) => {
        const nextErrors = { ...prev };
        delete nextErrors.privileges;
        return nextErrors;
      });
    }

    setFormData((prev) => {
      const currentPrivileges = Array.isArray(prev.privileges)
        ? prev.privileges
        : [];

      const privilegeExists = currentPrivileges.includes(privilegeValue);

      let newPrivileges;
      if (privilegeExists) {
        newPrivileges = currentPrivileges.filter((p) => p !== privilegeValue);
      } else {
        newPrivileges = [...currentPrivileges, privilegeValue];
      }

      return {
        ...prev,
        privileges: newPrivileges,
      };
    });
  };

  const formatFeeName = (fee) => {
    if (!fee) return "Unknown Fee";

    if (fee.description) {
      return fee.description;
    }

    return "Fee";
  };

  const getFeeById = (feeId) => {
    if (!feeId) return null;
    const stringFeeId = feeId.toString();
    return feesList.find(
      (fee) =>
        fee._id?.toString() === stringFeeId ||
        fee.id?.toString() === stringFeeId,
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: { width: { xs: "100%", sm: 520 } },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <form className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow">
            <h2 className="text-2xl font-bold mb-6">
              {selectedPlan ? "Edit Plan" : "Create New Plan"}
            </h2>

            <BasicInformationSection
              formData={formData}
              handleInputChange={handleFormInputChange}
              paystackPlanOptions={paystackPlanOptions}
              isPaystackPlansLoading={isPaystackPlansLoading}
              errors={errors}
            />

            <PricingSection
              formData={formData}
              handleInputChange={handleFormInputChange}
              errors={errors}
            />

            <FeaturesSection
              formData={formData}
              handleAddFeature={handleAddFeature}
              handleRemoveFeature={handleRemoveFeature}
              errors={errors}
            />

            <PrivilegesSection
              formData={formData}
              privilegeOptions={privilegeOptions}
              isPrivilegesLoading={isPrivilegesLoading}
              isPrivilegesError={isPrivilegesError}
              privilegesErrorMessage={privilegesErrorMessage}
              onRetryPrivileges={refetchPrivileges}
              handlePrivilegeChange={handlePrivilegeChange}
              handleSelectAllPrivileges={handleSelectAllPrivileges}
              handleDeselectAllPrivileges={handleDeselectAllPrivileges}
              errors={errors}
            />

            <FeesSection
              formData={formData}
              feesList={feesList}
              isFeesLoading={isFeesLoading}
              handleFeeSelection={handleFeeSelection}
              handleSelectAllFees={handleSelectAllFees}
              handleDeselectAllFees={handleDeselectAllFees}
              isFeeSelected={isFeeSelected}
              formatFeeName={formatFeeName}
              getFeeById={getFeeById}
              getPaystackPlanLabel={getPaystackPlanLabel}
              errors={errors}
            />

            <AdditionalSettingsSection
              formData={formData}
              handleInputChange={handleFormInputChange}
            />

            <div className="flex justify-between space-x-4">
              <button
                type="button"
                className="px-6 py-3 bg-white text-black border border-gray-300 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                onClick={handleCloseDrawer}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReviewPlan}
                disabled={isPendingUpdate || isPending}
                className="px-6 py-3 bg-primary1 text-white rounded-lg"
              >
                {selectedPlan
                  ? isPendingUpdate
                    ? "Updating..."
                    : "Review Changes"
                  : isPending
                    ? "Creating..."
                    : "Review Plan"}
              </button>
            </div>
          </form>
        </Box>
      </Drawer>

      <ConfirmationDrawer
        showConfirmation={showConfirmation}
        handleBackToForm={handleBackToForm}
        selectedPlan={selectedPlan}
        formData={formData}
        handleFinalSubmit={handleFinalSubmit}
        isPendingUpdate={isPendingUpdate}
        isPending={isPending}
        privilegeOptions={privilegeOptions}
        getFeeById={getFeeById}
        formatFeeName={formatFeeName}
        getPaystackPlanLabel={getPaystackPlanLabel}
      />
    </>
  );
}

export default EditPlan;
